// ─────────────────────────────────────────────
// pages/PricingPage.js
// Pricing page showing load tiers
// License: MIT (your original IP)
// ─────────────────────────────────────────────

import React, { useEffect } from "react";
import { BRAND_NAME } from "../constants";
import PageShell from "../components/PageShell";
import { EVENTS, trackEvent, trackPageView } from "../analytics/eventTracker";

const TIERS = [
  {
    name: "Small Load", range: "About a quarter truck bed", badge: "STARTING TIER", badgeType: "green",
    price: 75, fill: 18, desc: "A few trash bags, boxes, or loose household items.",
    chips: ["Trash bags", "Small boxes", "Loose items"],
  },
  {
    name: "Medium Load", range: "About half a truck bed", badge: "MOST COMMON", badgeType: "green",
    price: 120, fill: 42, highlight: true, desc: "Several bags, boxes, or loose items. Good for small cleanouts.",
    chips: ["Mixed bags", "Boxes", "Small cleanout"],
  },
  {
    name: "Heavy Load", range: "About three quarters of a truck bed", badge: "PCS READY", badgeType: "blue",
    price: 175, fill: 72, desc: "Large amount of bags, boxes, or yard debris. No large furniture or appliances.",
    chips: ["Yard debris", "Bulk bags", "Move-out haul"],
  },
  {
    name: "Full Load", range: "Full truck bed", badge: "MAX HAUL", badgeType: "blue",
    price: 295, fill: 100, desc: "Maximum truck capacity. Full cleanout or high-volume haul.",
    chips: ["Full cleanout", "High volume", "Max haul"],
  },
];

const BADGE_STYLES = {
  green: { background: "rgba(3,166,106,0.1)", color: "#03A66A", border: "1px solid rgba(3,166,106,0.25)" },
  blue:  { background: "rgba(4,135,217,0.08)", color: "#3D90D9", border: "1px solid rgba(4,135,217,0.2)" },
};

function fillBarBg(tier) {
  if (tier.name === "Small Load") return "#3D90D9";
  if (tier.name === "Full Load") return "#03A66A";
  return "linear-gradient(90deg, #3D90D9, #03A66A)";
}

export default function PricingPage({ nav, session }) {
  const userId = session?.user?.id || null;

  useEffect(() => {
    trackPageView("pricing", userId);
    trackEvent(EVENTS.PRICING_VIEWED, userId, {
      intent: "customer_booking",
      role_context: "customer",
      surface: "resident_app",
      page: "pricing",
    });
  }, [userId]);

  return (
    <PageShell style={{ fontFamily: "Barlow, sans-serif" }}>
      {/* Page header band */}
      <div style={{ background: "#071826", padding: "24px 20px 20px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase", color: "#3D90D9", marginBottom: 6 }}>{BRAND_NAME.toUpperCase()} SERVICES</div>
        <h2 style={{ fontWeight: 800, fontSize: 32, color: "#F2F2F2", margin: "0 0 8px", fontFamily: "Barlow Condensed, Barlow, sans-serif" }}>Pricing</h2>
        <p style={{ color: "#7a99b8", fontSize: 13, margin: 0 }}>Review your classified tier before booking.</p>
      </div>

      <div style={{ padding: "18px 18px 0" }}>
        {/* How price is set */}
        <div style={{ background: "#0d2035", borderRadius: 14, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "#3D90D9", marginBottom: 8 }}>HOW YOUR PRICE IS SET</div>
          <p style={{ fontSize: 15, fontWeight: 700, color: "#F2F2F2", lineHeight: 1.5, margin: 0 }}>Price is based on how much space your items take up in the truck. More items means a higher tier. LoadDash classifies your tier from photos and item details before you pay. If the actual load does not match the booking, the tier can be reviewed before service continues.</p>
        </div>

        {/* Tier cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
          {TIERS.map((t) => {
            const badge = BADGE_STYLES[t.badgeType];
            return (
              <div key={t.name} style={{ background: "#ffffff", border: t.highlight ? "1.5px solid #03A66A" : "1px solid #e0e8ef", borderRadius: 14, padding: 16 }}>
                {/* Top row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 20, color: "#0d1f2d", marginBottom: 2, fontFamily: "Barlow Condensed, Barlow, sans-serif" }}>{t.name}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#3D90D9", marginBottom: 6 }}>{t.range}</div>
                    <div style={{ display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: 0.5, padding: "3px 8px", borderRadius: 6, ...badge }}>{t.badge}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 36, color: "#3D90D9", lineHeight: 1, fontFamily: "Barlow Condensed, Barlow, sans-serif" }}>${t.price}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#8faabb", marginTop: 2 }}>flat rate</div>
                  </div>
                </div>

                {/* Truck fill bar */}
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#8faabb" }}>TRUCK FILL</span>
                  <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#8faabb" }}>{t.fill}%</span>
                </div>
                <div style={{ height: 7, background: "#e4ecf3", borderRadius: 4, marginBottom: 12 }}>
                  <div style={{ width: `${t.fill}%`, height: "100%", background: fillBarBg(t), borderRadius: 4 }} />
                </div>

                {/* Divider */}
                <div style={{ height: 1, background: "#e0e8ef", marginBottom: 12 }} />

                {/* Description */}
                <div style={{ fontSize: 13, color: "#4a6174", lineHeight: 1.5, marginBottom: 10 }}>{t.desc}</div>

                {/* Chips */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {t.chips.map(c => (
                    <span key={c} style={{ fontSize: 11, color: "#4a6174", background: "#f2f5f8", border: "1px solid #e0e8ef", borderRadius: 20, padding: "4px 10px", whiteSpace: "nowrap" }}>{c}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Notice block */}
        <div style={{ background: "#ffffff", border: "1px solid #e0e8ef", borderRadius: 12, padding: "13px 14px", marginBottom: 16, fontSize: 12.5, color: "#4a6174", lineHeight: 1.55 }}>
          <strong style={{ color: "#0d1f2d" }}>$15 per added piece</strong> collected at pickup. <strong style={{ color: "#0d1f2d" }}>Tires not accepted.</strong> Items over 50 lbs require two people to move.
        </div>

        {/* CTA */}
        <button onClick={() => nav && nav("schedule")} style={{ width: "100%", padding: 16, borderRadius: 12, border: "none", background: "#3D90D9", color: "#fff", fontWeight: 700, fontSize: 16, cursor: "pointer", fontFamily: "Barlow, sans-serif", marginBottom: 32 }}>Book a Pickup</button>
      </div>
    </PageShell>
  );
}
